import React from 'react';
import { format } from 'date-fns';
import { CloudOff, Cloud, Wind, Haze, Sun, HelpCircle } from 'lucide-react';
import {
  useNightVerdict,
  type NightConditions,
  type NightVerdict,
  type VerdictBlocker,
} from '@/hooks/useNightVerdict';

/**
 * Tonight and tomorrow night: will the weather cooperate? (ERI-1980)
 *
 * Premium only, rendered beside the calendar. Weather only: cloud, smoke and
 * wind. The calendar already shows darkness, the Milky Way and the moon, and this
 * panel says nothing about them. Two nights, deliberately, because that is as far
 * as a forecast is worth reading for this.
 *
 * THE PANEL IS ALLOWED TO SAY IT DOES NOT KNOW
 * --------------------------------------------
 * This is the whole reason the feature uses a model that returns a calibrated
 * confidence rather than a hand-written score. A formula can print "6 out of 10"
 * and cannot tell you how much to trust that number. Here, a night the model is
 * unsure about shows its conditions and no verdict, and a forecast that is not
 * readable says so. Never fake certainty at somebody about to drive two hours in
 * the dark.
 */

interface NightVerdictPanelProps {
  locationId?: string;
  locationName?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
}

const VERDICT_COPY: Record<NightVerdict, { label: string; color: string }> = {
  go: { label: 'Clear enough to go', color: 'text-green-500' },
  maybe: { label: 'Could go either way', color: 'text-yellow-500' },
  no_go: { label: 'Weather says no', color: 'text-red-500' },
};

const BLOCKER_COPY: Record<VerdictBlocker, { text: string; Icon: typeof Cloud }> = {
  cloud: { text: 'Cloud cover', Icon: Cloud },
  smoke: { text: 'Smoke and haze cutting transparency', Icon: Haze },
  wind: { text: 'Wind strong enough to shake the tripod', Icon: Wind },
  nothing: { text: 'Nothing in the weather working against it', Icon: Sun },
};

/** The worst value of `pick` across every sample point and hour, or null. */
function worst(
  conditions: NightConditions,
  pick: (hour: NightConditions['samples'][number]['hours'][number]) => number | null,
): number | null {
  const values: number[] = [];
  for (const sample of conditions.samples) {
    for (const hour of sample.hours) {
      const v = pick(hour);
      if (v !== null) values.push(v);
    }
  }
  return values.length ? Math.max(...values) : null;
}

const ConditionLines: React.FC<{ conditions: NightConditions }> = ({ conditions }) => {
  const cloud = worst(conditions, (h) => h.cloudCover);
  const low = worst(conditions, (h) => h.low);
  const mid = worst(conditions, (h) => h.mid);
  const high = worst(conditions, (h) => h.high);
  const wind = worst(conditions, (h) => h.windSpeed);
  const gust = worst(conditions, (h) => h.windGust);
  return (
    <ul className="space-y-1 text-xs text-muted-foreground" data-testid="night-conditions">
      {cloud !== null && (
        <li>
          Cloud peaks at {cloud}% after dark
          {low !== null && mid !== null && high !== null
            ? ` (low ${low}%, mid ${mid}%, high ${high}%)`
            : ''}
          .
        </li>
      )}
      <li>Transparency: {conditions.smoke.blurb}.</li>
      {wind !== null && (
        <li>
          Wind up to {Math.round(wind)} mph
          {gust !== null ? `, gusts to ${Math.round(gust)} mph` : ''}.
        </li>
      )}
    </ul>
  );
};

const OneNight: React.FC<NightVerdictPanelProps & { date: Date; heading: string }> = ({
  date,
  heading,
  ...location
}) => {
  const { data, loading, error } = useNightVerdict({ ...location, date });

  if (loading) {
    return (
      <div className="space-y-2 p-3 bg-muted/50 border rounded-lg">
        <p className="text-sm font-medium">{heading}</p>
        <p className="text-xs text-muted-foreground">Reading the forecast...</p>
      </div>
    );
  }

  // An error here is a quiet feature, not a broken page. Say nothing loudly.
  if (error || !data) return null;

  if (data.shownAs === 'unreadable') {
    return (
      <div className="space-y-2 p-3 bg-muted/50 border rounded-lg">
        <p className="text-sm font-medium">{heading}</p>
        <p className="text-xs text-muted-foreground">
          {data.reason === 'no_darkness'
            ? 'No real darkness tonight at this latitude, so there is no forecast to read.'
            : "The forecast is not readable. Check back closer to dark."}
        </p>
        {data.conditions && <ConditionLines conditions={data.conditions} />}
      </div>
    );
  }

  if (data.shownAs === 'verdict' && data.verdict) {
    const copy = VERDICT_COPY[data.verdict];
    const blocker = data.blocker ? BLOCKER_COPY[data.blocker] : null;
    const BlockerIcon = blocker?.Icon ?? Cloud;

    return (
      <div className="space-y-2 p-3 bg-muted/50 border rounded-lg">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium">{heading}</p>
          <span className={`text-sm font-semibold ${copy.color}`}>{copy.label}</span>
        </div>
        {blocker && data.blocker !== 'nothing' && (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <BlockerIcon className="w-3.5 h-3.5 shrink-0" />
            {blocker.text}
          </p>
        )}
        {data.bestHours && data.bestHours !== 'none' && (
          <p className="text-xs text-muted-foreground">
            Best weather starts around{' '}
            {format(new Date(`${data.bestHours}:00:00`), 'h a')}.
          </p>
        )}
        {data.conditions && <ConditionLines conditions={data.conditions} />}
      </div>
    );
  }

  // Not confident enough to call it. Show the numbers and hand it back.
  return (
    <div className="space-y-2 p-3 bg-muted/50 border rounded-lg">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">{heading}</p>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <HelpCircle className="w-3.5 h-3.5 shrink-0" />
          Your call
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        {data.modelUnavailable
          ? "Couldn't get a verdict just now. Here is what the forecast says."
          : 'The forecast is too mixed to call this one either way. Here is what it says.'}
      </p>
      {data.conditions && <ConditionLines conditions={data.conditions} />}
    </div>
  );
};

export const NightVerdictPanel: React.FC<NightVerdictPanelProps> = (props) => {
  if (!props.locationId || props.latitude === undefined || props.longitude === undefined) {
    return null;
  }

  const tonight = new Date();
  const tomorrow = new Date(tonight);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return (
    <div className="space-y-3" data-testid="night-verdict-panel">
      <div className="flex items-center gap-2">
        <CloudOff className="w-4 h-4 text-gold shrink-0" />
        <p className="text-sm font-medium">
          Tonight at {props.locationName ?? 'this location'}
        </p>
      </div>
      <OneNight {...props} date={tonight} heading="Tonight" />
      <OneNight {...props} date={tomorrow} heading="Tomorrow night" />
    </div>
  );
};

export default NightVerdictPanel;
